import type { Colors } from 'ui/styles/utils/colors';
import { resolveColor, getColorsMap } from 'ui/styles/utils/colors';
import Padding from 'ui/types/padding';
import styled, { css, ExecutionContext } from 'styled-components';

export interface TagProps {
  $color?: Colors;
  $compact?: boolean;
  $truncate?: boolean;
  $padding?: Padding;
  $raised?: boolean;
}

export interface TagContentProps {
  $color?: Colors;
  $textColor?: Colors;
}

function getDefaultStyle({ theme }: ExecutionContext & TagProps) {
  return css<TagProps>`
    display: block;
    padding-left: ${theme.tag.paddingX}rem;
    padding-right: ${theme.tag.paddingX}rem;
    border-radius: 4px;
    line-height: 140%;
    white-space: nowrap;
    align-items: center;
  `;
}

function getRaised({ theme, $raised }: ExecutionContext & TagProps) {
  if (!$raised) return null;

  return css<TagProps>`
    box-shadow: ${theme.tag.raisedBoxShadow};
  `;
}

function getCompact() {
  return css`
    display: inline-block;
  `;
}

function getFontSize({ theme }: ExecutionContext & TagProps) {
  return css<TagProps>`
    font-size: ${theme.typography.sizes.tiny}rem;
  `;
}

function getPadding({ theme, $padding }: ExecutionContext & TagProps) {
  if (!$padding) return null;

  const padding = theme.tag.paddingY * theme.padding[$padding];

  return css<TagProps>`
    padding-top: ${padding}rem;
    padding-bottom: ${padding}rem;
  `;
}

function getTruncation({ $truncate }: ExecutionContext & TagProps) {
  if (!$truncate) return null;

  return css`
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    display: inline-flex;
  `;
}

function getBackgroundColor({ theme, $color }: ExecutionContext & TagProps) {
  const themeColor = resolveColor(theme, $color);

  return css`
    background-color: ${themeColor};
  `;
}

function getTextColor({ theme, $textColor, $color }: ExecutionContext & TagContentProps) {
  const { minor } = getColorsMap(theme, $color);
  const textColor = $textColor || minor;

  return css`
    color: ${textColor};
  `;
}

export const Tag = styled.span<TagProps>`
  ${getDefaultStyle};
  ${getRaised};
  ${getCompact};
  ${getFontSize};
  ${getPadding};
  ${getTruncation};
  ${getBackgroundColor};
`;

export const Content = styled.span<TagContentProps>`
  ${getTextColor};
`;
